import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Button, Dialog, DialogActions, Field, Select, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";
import { ago, compact, fmtDate, fmtDateTime, fmtTime, num, rolesLabel, runLabel, serviceNames, serviceTone } from "../lib/format";
import { keys, useAction, useAudit, useDevices, useJobs, useMachines, useMembers, useRuns, useSessions, useSummary, useUsage } from "../lib/queries";
import type { AuditRow, CustomerSummary, Me, Member, RoleView } from "../lib/types";
import { ConsoleShell } from "../console/Shell";
import { Empty, ErrorBox, InfoRow, Loading, NotReady, Pager, Panel, StatGrid, StatusBadge } from "../console/ui";
import { urlParam } from "../console/metrics";
import { JobTitleField, jobTitleOf } from "../console/job-title";
import { CreateTaskDialog } from "./create-task";
import { FilteredMachineCards, RegisterMachineDialog } from "./machines";
import { MachineDetailDialog } from "./machine-detail";
import { SeoAnalyticsScreen } from "./analytics";
import { CustomerBillingScreen } from "./billing";
import { MachineStatusTable, SystemStatusView } from "./surface";
import { useCustomerStatus } from "../lib/queries";
import { CustomerResultsPanel, CustomerUsageCharts } from "./monitoring";
import { ACTIVE_FILTER, jobTitle, RunDetailDialog, runFilterParams, RunTable, useRunExtraFilters } from "./runs";
import { ResultReviewScreen, TenantIntroductionPanel, useReviewFocus } from "./review";
import { NewPublishDialog, PublishDetailDialog, PublishList, usePublishFocus } from "./customer-publish";
import { AssignRolesDialog, CustomerRolesPanel, RoleChecklist, RoleChips, useTenantRoles } from "./roles";
import { ReadableDetails } from "@studio-features/meta-crew/readable-details";
import { actionLabel, auditDetailView, auditSummary } from "../console/readable";
import { MaskedValue } from "../console/masked";

export function CustomerPage({ me, customerId, screen, machineId }: { me: Me; customerId: string; screen: string; machineId?: string }) {
  const summary = useSummary(customerId);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [pairOpen, setPairOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [memberOpen, setMemberOpen] = useState(false);
  // 고객 발행 요청 상세 (헤더 알림·목록에서 연다). 직원은 기존 실행 상세가 고객 요청이면 같은 상세로 넘긴다.
  const publishRunId = usePublishFocus((s) => s.runId);
  const openPublish = usePublishFocus((s) => s.open);
  // 2026-10-21 요청 25: 디바이스 화면은 없어졌다 → 머신 목록 (폰은 머신 상세 › 디바이스)
  useEffect(() => {
    if (screen === "devices") void navigate({ to: "/c/$customerId/$screen", params: { customerId, screen: "machines" }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, customerId]);
  if (summary.isLoading) return <Loading />;
  if (summary.error || !summary.data) return <ErrorBox error={errorMessage(summary.error)} onRetry={() => void summary.refetch()} />;
  const customer = summary.data;
  const operable = customer.status === "active" || customer.status === "overdue";
  // 2026-10-16 고객 발행: 고객사 회원은 PUBLISH_WRITE 로 "새 발행"(블로그 발행 요청)을 보낸다.
  const canCreate = can(me, "TASK_CREATE", "PUBLISH_WRITE") && operable;
  const publisher = !me.isStaff;
  const openMachine = (id: string) => void navigate({ to: "/c/$customerId/machines/$machineId", params: { customerId, machineId: id } });
  // search: KPI 카드가 넘기는 첫 필터 (2026-10-21)
  const go = (s: string, search?: Record<string, string>) => void navigate({ to: "/c/$customerId/$screen", params: { customerId, screen: s }, ...(search ? { search: search as never } : {}) });
  // 시안 e4ed46c: 화면마다 등록 동작 하나를 우측 하단 FAB 로. 정지·해지 고객사는 작업 등록·회원 초대를 하지 않는다 (직원도).
  const fab =
    machineId
      ? undefined
      : ["overview", "tasks", "task-schedules"].includes(screen) && canCreate
        ? { label: publisher ? "새 발행" : "새 작업", onClick: () => setCreateOpen(true) }
        : screen === "machines" && can(me, "MACHINE_PAIR") && operable
          ? { label: "머신 등록", onClick: () => setPairOpen(true) }
          : screen === "members" && can(me, "MEMBER_MANAGE") && operable
            ? { label: "회원 초대", onClick: () => setMemberOpen(true) }
            : undefined;

  // 해지된 고객사의 고객 계정은 세션 종료 안내만 (직원은 상태 조회를 위해 그대로 연다).
  if (!me.isStaff && customer.status === "terminated") {
    return (
      <div className="mcc-fallback">
        <div className="mcc-fallback-card">
          <h1>서비스가 해지되었습니다</h1>
          <p>조직의 로그인 세션이 종료되었습니다. 이용 재개는 담당자에게 문의해 주세요.{customer.support ? ` 문의: ${customer.support}` : ""}</p>
        </div>
      </div>
    );
  }

  return (
    <ConsoleShell me={me} scope="customer" screen={machineId ? "machines" : screen} customer={customer} fab={fab}>
      {machineId || screen === "machines" ? (
        // 시안 06a1acb: 머신 목록 위에 공통 상세 다이얼로그 (/machines/:machineId 주소는 다이얼로그를 연다)
        me.isStaff ? (
          <MachinesScreen customerId={customerId} onOpen={openMachine} />
        ) : (
          <CustomerMachineList customerId={customerId} onOpen={openMachine} />
        )
      ) : screen === "overview" ? (
        <>
          {/* 시안 d074369: 현황 = 시스템 상태 개요 (작업 내용 없음). 직원에게는 아래에 최근 작업을 더한다. */}
          <SystemStatusView customerId={customerId} customerName={customer.name} onNavigate={go} onMachine={openMachine} canAnalyze={can(me, "MARKETING_READ")} />
          {me.isStaff && can(me, "TASK_DETAIL") && <StaffRecentRuns customerId={customerId} onOpenRun={setRunId} />}
        </>
      ) : screen === "tasks" ? (
        publisher ? (
          // 시안 951f2f6 고객 발행: 자기 요청(origin=customer)만, 카드 목록 → 작업 상세. 새 발행은 FAB.
          <PublishList customerId={customerId} canWrite={canCreate} approval={customer.publishApproval ?? "staff"} onCreate={() => setCreateOpen(true)} onOpen={openPublish} />
        ) : (
          <CustomerTasks customerId={customerId} onOpenRun={setRunId} canEdit={canCreate} view="runs" publisher={false} approval={customer.publishApproval ?? "staff"} />
        )
      ) : screen === "task-schedules" ? (
        <CustomerTasks customerId={customerId} onOpenRun={setRunId} canEdit={canCreate} view="schedules" />
      ) : screen === "reviews" ? (
        me.isStaff && can(me, "TASK_DETAIL") ? <ResultReviewScreen me={me} customerId={customerId} /> : null
      ) : screen === "marketing" ? (
        <SeoAnalyticsScreen customerId={customerId} staffView={me.isStaff} onManage={me.isStaff ? () => go("settings") : undefined} />
      ) : screen === "usage" ? (
        <UsageScreen customer={customer} me={me} go={go} />
      ) : screen === "billing" ? (
        <CustomerBillingScreen me={me} customer={customer} />
      ) : screen === "members" ? (
        <MembersScreen me={me} customerId={customerId} open={memberOpen} setOpen={setMemberOpen} />
      ) : screen === "profile" ? (
        <ProfileScreen me={me} />
      ) : screen === "settings" ? (
        <>
          {me.isStaff && <TenantIntroductionPanel customer={customer} canEdit={can(me, "CUSTOMER_MANAGE")} />}
          <CustomerSettingsScreen me={me} customer={customer} />
        </>
      ) : screen === "customer-roles" ? (
        <CustomerRolesPanel me={me} customerId={customerId} customerName={customer.name} suspended={!["active", "overdue"].includes(customer.status)} />
      ) : screen === "audit" ? (
        <CustomerAudit customerId={customerId} />
      ) : null}
      {publisher ? (
        <NewPublishDialog customerId={customerId} approval={customer.publishApproval ?? "staff"} open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => id && openPublish(id)} />
      ) : (
        <CreateTaskDialog customerId={customerId} open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => id && setRunId(id)} />
      )}
      {publishRunId && (
        <PublishDetailDialog
          key={publishRunId}
          customerId={customerId}
          runId={publishRunId}
          staff={me.isStaff}
          onClose={() => openPublish(null)}
          onOpenRun={(id) => openPublish(id)}
          onRun={me.isStaff ? () => { const id = publishRunId; openPublish(null); setRunId(id); } : undefined}
        />
      )}
      <RegisterMachineDialog customerId={customerId} open={pairOpen} onClose={() => setPairOpen(false)} />
      <MachineDetailDialog me={me} customerId={customerId} machineId={machineId ?? null} customerStatus={customer.status} onClose={() => go("machines")} onOpenTask={setRunId} />
      <RunDetailDialog
        runId={runId}
        customerId={customerId}
        onClose={() => setRunId(null)}
        onReview={(taskId) => {
          useReviewFocus.getState().focus(taskId);
          setRunId(null);
          go("reviews");
        }}
      />
    </ConsoleShell>
  );
}

function CustomerOverview({
  me,
  customer,
  onOpenRun,
  onMachine,
  go,
}: {
  me: Me;
  customer: CustomerSummary;
  onOpenRun: (id: string) => void;
  onMachine: (id: string) => void;
  go: (s: string) => void;
}) {
  const machines = useMachines(customer.id);
  const devices = useDevices(customer.id);
  const runs = useRuns(customer.id, { limit: 8 });
  const list = machines.data?.items ?? [];
  const online = list.filter((m) => m.connected).length;
  const paired = list.filter((m) => m.pairedAt).length;
  // 고객 응답에는 quota 가 없다 (LLM 작업 숫자는 직원 전용). 새 시안 전까지 화면이 깨지지 않게만.
  const q = customer.quota ?? { used: 0, tasksPerMonth: null, inFlight: 0, tokens: { input: 0, cached: 0, output: 0, reasoning: 0 } } as unknown as NonNullable<CustomerSummary["quota"]>;
  const sub = customer.subscription;
  return (
    <>
      <StatGrid
        items={[
          ["온라인 머신", `${online} / ${paired}`, paired === 0 ? "연결된 PC 없음" : online === paired ? "연결 상태 정상" : `${paired - online}대 확인 필요`, online === paired ? "teal" : "amber"],
          ["등록 디바이스", devices.data?.total ?? 0, "고객사 소유 기기"],
          ["이번 달 작업", num(q.used), q.tasksPerMonth ? `${num(q.tasksPerMonth)}건 중 사용` : "한도 없음"],
          ["서비스 상태", serviceNames[customer.status], sub ? `${sub.planName ?? sub.planCode} · ${sub.daysLeft}일 남음` : "플랜 없음", serviceTone[customer.status]],
        ]}
      />
      <div className="meta-crew-grid-main">
        <div>
          <Panel
            title="현장 상태"
            extra={
              <Button size="sm" variant="ghost" onClick={() => go("machines")}>
                머신 전체
              </Button>
            }
          >
            {list.length === 0 && (
              <InfoRow
                title="등록된 머신이 없습니다"
                description={can(me, "MACHINE_PAIR") ? "머신 메뉴에서 PC를 등록하고 페어링 코드를 발급하세요." : "현장 PC 설치와 연결은 담당자가 진행합니다. 연결되면 여기에 표시됩니다."}
              />
            )}
            {list.map((m) => (
              <InfoRow
                key={m.id}
                title={m.label}
                description={
                  !m.pairedAt
                    ? "현장 페어링 대기"
                    : m.connected
                      ? `디바이스 ${m.deviceCount ?? 0}대 · ${m.lastState?.llm?.available ? "AI 로그인됨" : "AI 로그인 필요"}`
                      : `마지막 수신 ${ago(m.lastSeenAt)} · 전원과 네트워크를 확인하세요`
                }
              >
                <StatusBadge label={!m.pairedAt ? ["미연결", "neutral"] : m.connected ? ["온라인", "teal"] : ["현장 조치", "coral"]} />
                <Button size="sm" variant="secondary" onClick={() => onMachine(m.id)}>
                  머신 상세
                </Button>
              </InfoRow>
            ))}
          </Panel>
          {can(me, "TASK_DETAIL") && (
            <Panel title="최근 작업">
              {(runs.data?.items ?? []).length === 0 && <InfoRow title="아직 작업이 없습니다" description={can(me, "TASK_CREATE") ? "새 작업으로 등록하세요." : "작업은 담당자가 등록합니다. 진행과 결과를 여기서 볼 수 있습니다."} />}
              {(runs.data?.items ?? []).map((r) => (
                <InfoRow key={r.id} title={jobTitle(r.jobName)} description={`${fmtTime(r.startedAt ?? r.createdAt)} · ${(r.resultSummary ?? r.errorMessage ?? "").slice(0, 70)}`}>
                  <StatusBadge label={runLabel(r.status)} />
                  <Button size="sm" variant="ghost" onClick={() => onOpenRun(r.id)}>
                    상세
                  </Button>
                </InfoRow>
              ))}
            </Panel>
          )}
        </div>
        <div>
          <CustomerResultsPanel customerId={customer.id} />
          {can(me, "USAGE_READ", "BILLING_READ") && (
            <Panel title="이번 달 이용">
              <InfoRow title={sub ? `${sub.planName ?? sub.planCode}` : "플랜 없음"} description={`작업 ${num(q.used)}${q.tasksPerMonth ? ` / ${num(q.tasksPerMonth)}` : ""}건 · 진행 중 ${q.inFlight}건`} />
              {sub && <InfoRow title={`기간 종료 ${fmtDate(sub.currentPeriodEnd)}`} description={sub.planCode === "trial" ? "체험 기간이 끝나면 새 작업이 중지됩니다." : "포함량은 매월 갱신됩니다."} />}
              <div className="mcc-padding">
                <Button variant="secondary" size="sm" onClick={() => go(can(me, "USAGE_READ") ? "usage" : "billing")}>
                  사용량과 결제 확인
                </Button>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}

function CustomerTasks({
  customerId,
  onOpenRun,
  canEdit,
  view,
  publisher = false,
  approval = "staff",
}: {
  customerId: string;
  onOpenRun: (id: string) => void;
  canEdit: boolean;
  view: "runs" | "schedules";
  publisher?: boolean;
  approval?: "staff" | "auto";
}) {
  const canCreate = canEdit;
  // KPI 카드에서 오면 ?status= 로 첫 필터 (2026-10-21)
  const [status, setStatus] = useState(() => urlParam("status") ?? "");
  const [offset, setOffset] = useState(0);
  const extra = useRunExtraFilters(() => setOffset(0));
  const runs = useRuns(customerId, { ...runFilterParams(status), ...extra.params, offset, limit: 30 });
  const jobs = useJobs(customerId);
  const machines = useMachines(customerId);
  const toggle = useAction((v: { id: string; enable: boolean }) => api.post(`/c/${customerId}/jobs/${v.id}/${v.enable ? "enable" : "disable"}`), [keys.jobs(customerId)]);
  const remove = useAction((id: string) => api.del(`/c/${customerId}/jobs/${id}`), [keys.jobs(customerId), ["c", customerId]]);
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      {publisher ? (
        <InfoRow
          title={approval === "auto" ? "접수 즉시 발행됩니다" : "운영팀 확인 뒤 발행됩니다"}
          description={canCreate ? "새 발행으로 원고와 사진을 보내면 여기에서 진행 상황과 결과를 볼 수 있습니다." : "발행 요청은 고객사 대표·관리자가 보냅니다. 진행 상황과 결과는 여기서 볼 수 있습니다."}
        />
      ) : (
        !canCreate && <InfoRow title="작업은 담당자가 등록합니다" description="등록된 작업의 진행 상황과 결과를 여기서 확인할 수 있습니다." />
      )}
      {view === "runs" ? (
        <RunTable
          extraFilters={extra.node}
          customerMode={publisher}
          machines={(machines.data?.items ?? []).map((m) => ({ id: m.id, label: m.label }))}
          rows={runs.data?.items ?? []}
          total={runs.data?.total ?? 0}
          offset={offset}
          limit={30}
          onOffset={setOffset}
          status={status}
          onStatus={(s) => {
            setStatus(s);
            setOffset(0);
          }}
          loading={runs.isLoading}
          error={runs.error ? errorMessage(runs.error) : null}
          onRetry={() => void runs.refetch()}
          onOpen={(r) => onOpenRun(r.id)}
        />
      ) : (
        <Panel title="예약 · 반복 작업">
          {err && <InfoRow title={err} />}
          {(jobs.data?.items ?? []).length === 0 && <InfoRow title="예약된 작업이 없습니다" description="새 작업에서 '예약' 또는 '반복'을 선택하세요." />}
          {(jobs.data?.items ?? []).map((j) => (
            <InfoRow
              key={j.id}
              title={j.name ?? "예약 작업"}
              description={`${j.schedule ? cronText(j.schedule) : `한 번 · ${fmtDateTime(j.runAt)}`} · 다음 실행 ${j.enabled ? fmtDateTime(j.nextRunAt) : "중지됨"}`}
            >
              <Badge tone={j.enabled ? "teal" : "neutral"}>{j.enabled ? "예약됨" : "중지"}</Badge>
              {canEdit && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => toggle.mutateAsync({ id: j.id, enable: !j.enabled }).catch((e) => setErr(errorMessage(e)))}>
                    {j.enabled ? "일시정지" : "재개"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(`'${j.name ?? "예약 작업"}'을 삭제할까요? 대기 중인 실행도 취소됩니다.`)) remove.mutateAsync(j.id).catch((e) => setErr(errorMessage(e)));
                    }}
                  >
                    삭제
                  </Button>
                </>
              )}
            </InfoRow>
          ))}
        </Panel>
      )}
    </>
  );
}

const DOW = ["일", "월", "화", "수", "목", "금", "토"];
function cronText(cron: string): string {
  const [m, h, dom, mon, dow] = cron.trim().split(/\s+/);
  const time = /^\d+$/.test(h) && /^\d+$/.test(m) ? `${h.padStart(2, "0")}:${m.padStart(2, "0")}` : `${h}:${m}`;
  if (dom === "*" && mon === "*" && dow === "*") return `매일 ${time}`;
  if (dom === "*" && mon === "*" && /^\d$/.test(dow)) return `매주 ${DOW[Number(dow)]}요일 ${time}`;
  return `반복 (${cron})`;
}

function MachinesScreen({ customerId, onOpen }: { customerId: string; onOpen: (id: string) => void }) {
  const q = useMachines(customerId);
  const staff = !!useAuth((s) => s.me)?.isStaff;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  return <FilteredMachineCards rows={q.data?.items ?? []} onOpen={(m) => onOpen(m.id)} deletedUrl={staff ? `/c/${customerId}/machines?deleted=1` : undefined} />;
}

function UsageScreen({ customer, me, go }: { customer: CustomerSummary; me?: Me; go?: (s: string, search?: Record<string, string>) => void }) {
  const q = useUsage(customer.id);
  const u = q.data ?? customer.quota;
  const total = u.tokens.input + u.tokens.output + u.tokens.reasoning;
  return (
    <>
      <StatGrid
        links={{
          "진행 중": !!go && can(me, "TASK_DETAIL") && { onOpen: () => go("tasks", { status: ACTIVE_FILTER, scope: "all" }), label: "진행·대기 중인 작업 보기" },
          "구독 기간": !!go && can(me, "BILLING_READ") && { onOpen: () => go("billing"), label: "결제 화면에서 보기" },
        }}
        items={[
          ["LLM 작업", num(u.used), u.tasksPerMonth ? `${num(u.tasksPerMonth)}건 중 · 시작 전 취소·시스템 오류는 미산입` : "한도 없음"],
          ["토큰 사용", compact(total), `입력 ${compact(u.tokens.input)} · 캐시 ${compact(u.tokens.cached)} · 출력 ${compact(u.tokens.output)}`],
          ["진행 중", u.inFlight, "곧 산입될 수 있는 작업"],
          ["구독 기간", customer.subscription ? `${customer.subscription.daysLeft}일` : "-", customer.subscription ? `${fmtDate(customer.subscription.currentPeriodEnd)}까지` : "플랜 없음"],
        ]}
      />
      <Panel title="이번 달 한도">
        <InfoRow title={`작업 ${num(u.used)}${u.tasksPerMonth ? ` / ${num(u.tasksPerMonth)}` : ""}건`} description={`${fmtDate(u.periodStart)}부터 · ${u.hardStop ? "한도에 닿으면 새 작업을 보류합니다" : "초과분은 종량 과금"}`}>
          {u.tasksPerMonth ? <progress aria-label="작업 한도" value={u.used} max={u.tasksPerMonth} /> : null}
        </InfoRow>
        <InfoRow title={`머신 ${u.machines ?? "무제한"}대 · 디바이스 ${u.devices ?? "무제한"}대까지`} description="플랜 포함량" />
      </Panel>
      <CustomerUsageCharts customerId={customer.id} />
    </>
  );
}

function BillingScreen({ customer }: { customer: CustomerSummary }) {
  const sub = customer.subscription;
  return (
    <>
      <Panel title="현재 플랜">
        {sub ? (
          <>
            <InfoRow title={`${sub.planName ?? sub.planCode} · ${sub.period === "year" ? "연간" : "월간"}`} description={`${fmtDate(sub.currentPeriodStart)} ~ ${fmtDate(sub.currentPeriodEnd)} · ${sub.daysLeft}일 남음`}>
              <Badge tone={sub.status === "active" ? "teal" : "amber"}>{sub.status === "trialing" ? "체험" : sub.status === "active" ? "이용 중" : sub.status}</Badge>
            </InfoRow>
            {sub.promotion && (
              <InfoRow
                title={sub.endingSoon ? `프로모션이 ${fmtDate(sub.currentPeriodEnd)}에 끝납니다` : `프로모션 · ${fmtDate(sub.currentPeriodEnd)}까지 무료`}
                description={sub.endingSoon ? `계속 이용하려면 담당자에게 결제를 요청하세요.${customer.support ? ` 문의: ${customer.support}` : ""}` : "프로와 같은 기능·한도를 결제 없이 이용합니다."}
              />
            )}
            {sub.planCode === "trial" && <InfoRow title="체험 기간이 끝나면 서비스가 정지됩니다" description={`계속 이용하려면 담당자에게 플랜 전환을 요청하세요.${customer.support ? ` 문의: ${customer.support}` : ""}`} />}
          </>
        ) : (
          <InfoRow title="플랜 정보가 없습니다" />
        )}
      </Panel>
      <Panel title="결제 문의">
        <InfoRow title="결제와 플랜 변경은 담당자가 처리합니다" description={customer.support ? `문의: ${customer.support}` : "담당자에게 문의해 주세요."} />
      </Panel>
    </>
  );
}

/** 회원 목록. 초대는 페이지 FAB (open 상태를 부모가 가진다). */
function MembersScreen({ me, customerId, open, setOpen }: { me: Me; customerId: string; open: boolean; setOpen: (v: boolean) => void }) {
  const q = useMembers(customerId);
  const roles = useTenantRoles(customerId);
  const roleOptions = (roles.data?.roles ?? []).filter((r) => me.isStaff || r.code !== "CUSTOMER_OWNER");
  const manage = can(me, "MEMBER_MANAGE");
  const [assign, setAssign] = useState<Member | null>(null);
  return (
    <Panel title="고객사 회원">
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={errorMessage(q.error)} />
      ) : (q.data?.items ?? []).length === 0 ? (
        <Empty>아직 고객사 회원이 없습니다. 고객 화면은 관리자가 대신 관리합니다.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>이름</th>
              <th>이메일</th>
              <th>직책</th>
              <th>역할</th>
              <th>상태</th>
              <th>마지막 로그인</th>
              {manage && <th aria-label="관리" />}
            </tr>
          </thead>
          <tbody>
            {(q.data?.items ?? []).map((m) => (
              <tr key={m.id}>
                <td>
                  <MaskedValue value={m.name} source={{ url: `/c/${customerId}/members/${m.id}/pii`, pick: (d) => d.name }} />
                </td>
                <td>
                  <MaskedValue value={m.email} source={{ url: `/c/${customerId}/members/${m.id}/pii`, pick: (d) => d.email }} />
                </td>
                <td>{m.jobTitle || "-"}</td>
                <td>
                  <RoleChips roles={m.roles} />
                </td>
                <td>
                  <Badge tone={m.status === "active" ? "teal" : "neutral"}>{m.status === "active" ? "활성" : "중지"}</Badge>
                </td>
                <td>{m.lastLoginAt ? fmtDateTime(m.lastLoginAt) : "-"}</td>
                {manage && (
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setAssign(m)}>
                      역할 지정
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <AddMemberDialog open={open} onClose={() => setOpen(false)} path={`/c/${customerId}/members`} roleOptions={roleOptions} defaultHint="고르지 않으면 멤버" invalidate={keys.members(customerId)} />
      <AssignRolesDialog
        open={!!assign}
        onClose={() => setAssign(null)}
        memberName={assign?.name ?? ""}
        options={roleOptions}
        current={assign?.roles ?? []}
        path={`/c/${customerId}/members/${assign?.id ?? ""}/roles`}
        invalidate={[[...keys.members(customerId)]]}
      />
    </Panel>
  );
}

export function AddMemberDialog({
  open,
  onClose,
  path,
  roleOptions,
  defaultHint,
  invalidate,
  extra,
}: {
  open: boolean;
  onClose: () => void;
  path: string;
  /** 묶을 수 있는 역할 (RBAC). 고르지 않으면 서버 기본값. */
  roleOptions: RoleView[];
  defaultHint?: string;
  invalidate: readonly unknown[];
  extra?: Record<string, unknown>;
}) {
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  useEffect(() => {
    if (open) setRoleIds([]);
  }, [open]);
  const add = useAction((b: Record<string, unknown>) => api.post(path, b), [invalidate as unknown[]]);
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="회원 추가"
      description="초기 비밀번호는 본인에게 따로 전달하고, 첫 로그인 후 바꾸도록 안내하세요."
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: "추가", type: "submit", form: formId, disabled: add.isPending }} note={error ?? undefined} />}
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const d = new FormData(e.currentTarget);
          add
            .mutateAsync({ ...extra, email: d.get("email"), name: d.get("name"), phone: d.get("phone") || undefined, jobTitle: jobTitleOf(d) || undefined, ...(roleIds.length ? { roleIds } : {}), password: d.get("password") })
            .then(onClose)
            .catch((er) => setError(errorMessage(er)));
        }}
      >
        <Field name="name" label="이름" required />
        <Field name="email" label="이메일 (로그인 아이디)" type="email" required />
        <Field name="phone" label="연락처" />
        <JobTitleField />
        <div className="field">
          <span className="label">역할 (여러 개 고를 수 있음{defaultHint ? `, ${defaultHint}` : ""})</span>
          <RoleChecklist options={roleOptions} value={roleIds} onChange={setRoleIds} />
        </div>
        <Field name="password" label="초기 비밀번호 (10자 이상)" type="password" minLength={10} required autoComplete="new-password" />
      </form>
    </Dialog>
  );
}

export function ProfileScreen({ me }: { me: Me }) {
  const sessions = useSessions();
  const setMe = useAuth((s) => s.setMe);
  const [msg, setMsg] = useState<string | null>(null);
  const save = useAction((b: Record<string, unknown>) => api.patch<Me>("/auth/profile", b));
  const pw = useAction((b: Record<string, unknown>) => api.post("/auth/password", b), [keys.sessions]);
  const revoke = useAction((id: string) => api.del(`/auth/sessions/${id}`), [keys.sessions]);
  return (
    <>
      <Panel title="프로필">
        <form
          className="mcc-form mcc-padding"
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            save
              .mutateAsync({ name: d.get("name"), phone: d.get("phone") || undefined })
              .then((m) => {
                setMe(m);
                setMsg("저장했습니다.");
              })
              .catch((er) => setMsg(errorMessage(er)));
          }}
        >
          <Field name="name" label="이름" defaultValue={me.name ?? ""} required />
          <Field name="phone" label="연락처" placeholder="연락처 입력" />
          <InfoRow title={me.email ?? ""} description={rolesLabel(me)} />
          <Button type="submit" disabled={save.isPending}>
            저장
          </Button>
        </form>
      </Panel>
      <Panel title="비밀번호 변경">
        <form
          className="mcc-form mcc-padding"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const d = new FormData(form);
            if (d.get("newPassword") !== d.get("confirm")) return setMsg("새 비밀번호가 서로 다릅니다.");
            pw.mutateAsync({ currentPassword: d.get("currentPassword"), newPassword: d.get("newPassword") })
              .then(() => {
                form.reset();
                setMsg("비밀번호를 바꿨습니다. 다른 기기의 로그인은 모두 끊겼습니다.");
              })
              .catch((er) => setMsg(errorMessage(er)));
          }}
        >
          <Field name="currentPassword" label="현재 비밀번호" type="password" required autoComplete="current-password" />
          <Field name="newPassword" label="새 비밀번호 (10자 이상)" type="password" minLength={10} required autoComplete="new-password" />
          <Field name="confirm" label="새 비밀번호 확인" type="password" minLength={10} required autoComplete="new-password" />
          <Button type="submit" disabled={pw.isPending}>
            변경
          </Button>
        </form>
      </Panel>
      {msg && <InfoRow title={msg} />}
      <Panel title="로그인 세션">
        {(sessions.data?.items ?? []).map((s) => (
          <InfoRow key={s.id} title={`${s.current ? "현재 로그인 · " : ""}${s.deviceLabel ?? "알 수 없는 기기"}`} description={`${fmtDateTime(s.createdAt)} 로그인 · 마지막 사용 ${ago(s.lastUsedAt)} · ${s.ip ?? ""}`}>
            {!s.current && (
              <Button size="sm" variant="secondary" onClick={() => revoke.mutateAsync(s.id).catch((er) => setMsg(errorMessage(er)))}>
                세션 종료
              </Button>
            )}
          </InfoRow>
        ))}
      </Panel>
    </>
  );
}

/** 고객사 설정 (직원이 고객사를 열었을 때). 회원 권한은 조직 › 고객사 권한 (시안 ae6ed08). */
function CustomerSettingsScreen(_: { me: Me; customer: CustomerSummary }) {
  return (
    <Panel title="알림">
      <NotReady title="알림 채널">현장 조치·작업 실패·결제 안내 알림은 다음 단계에서 연결됩니다.</NotReady>
    </Panel>
  );
}

function CustomerAudit({ customerId }: { customerId: string }) {
  const [offset, setOffset] = useState(0);
  const q = useAudit(customerId, offset);
  return <AuditTable rows={q.data?.items ?? []} total={q.data?.total ?? 0} offset={offset} onOffset={setOffset} loading={q.isLoading} error={q.error ? errorMessage(q.error) : null} />;
}

export function AuditTable({
  rows,
  total,
  offset,
  onOffset,
  loading,
  error,
  showCustomer = false,
}: {
  rows: AuditRow[];
  total: number;
  offset: number;
  onOffset: (o: number) => void;
  loading: boolean;
  error: string | null;
  showCustomer?: boolean;
}) {
  // 요청서 23절 (시안 05be2fc): 원본 JSON 대신 읽기 쉬운 요약 + 상세(관리자만 접힌 원본)
  const staff = !!useAuth((x) => x.me)?.isStaff;
  const [open, setOpen] = useState<AuditRow | null>(null);
  if (error) return <ErrorBox error={error} />;
  if (loading) return <Loading />;
  return (
    <Panel title="감사 기록">
      {rows.length === 0 ? (
        <Empty>기록이 없습니다.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>시각</th>
              <th>누가</th>
              <th>무엇을</th>
              <th>대상</th>
              {showCustomer && <th>고객사</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.at}-${i}`}>
                <td>{fmtDateTime(r.at)}</td>
                <td>
                  {r.actorId && r.actorKind !== "machine" && r.actorKind !== "system" && (staff || r.actorCustomerId) ? (
                    <MaskedValue
                      value={r.actorName}
                      source={{ url: staff ? `/admin/members/${r.actorId}/pii` : `/c/${r.actorCustomerId}/members/${r.actorId}/pii`, pick: (d) => d.name }}
                    />
                  ) : (
                    (r.actorName ?? (r.actorKind === "system" ? "시스템" : "-"))
                  )}
                  <small className="meta-crew-sub">{r.actorKind === "machine" ? "머신" : r.actorKind === "system" ? "자동" : (r.client ?? "")}</small>
                </td>
                <td>{actionLabel(r.action)}</td>
                <td>
                  {r.targetType ?? ""}
                  <small className="meta-crew-sub">{auditSummary(r)}</small>{" "}
                  <Button size="sm" variant="ghost" onClick={() => setOpen(r)}>
                    상세
                  </Button>
                </td>
                {showCustomer && <td>{r.customerId ? r.customerId.slice(0, 8) : "-"}</td>}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Pager offset={offset} limit={50} total={total} onChange={onOffset} />
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)} title="감사 기록 상세" description={open ? actionLabel(open.action) : ""}>
        {open && <ReadableDetails view={auditDetailView(open, staff)} canReadDebug={staff} />}
      </Dialog>
    </Panel>
  );
}

/** 고객 웹 머신 목록: 상태 표 (처리 건수 없이 처리 중 여부만). */
function CustomerMachineList({ customerId, onOpen }: { customerId: string; onOpen: (id: string) => void }) {
  const q = useCustomerStatus(customerId);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  if (!q.data.machines.length) return <Empty>연결된 머신이 없습니다. 현장 PC 설치와 연결은 담당자가 진행합니다.</Empty>;
  return <MachineStatusTable rows={q.data.machines} onSelect={onOpen} />;
}

/** 직원이 고객사를 열었을 때만: 최근 작업 (고객에게는 작업이 없다). */
function StaffRecentRuns({ customerId, onOpenRun }: { customerId: string; onOpenRun: (id: string) => void }) {
  const runs = useRuns(customerId, { limit: 8 });
  return (
    <Panel title="최근 작업 · 관리자 전용">
      {(runs.data?.items ?? []).length === 0 && <InfoRow title="아직 작업이 없습니다" />}
      {(runs.data?.items ?? []).map((r) => (
        <InfoRow key={r.id} title={jobTitle(r.jobName)} description={`${fmtTime(r.startedAt ?? r.createdAt)} · ${(r.resultSummary ?? r.errorMessage ?? "").slice(0, 70)}`}>
          <StatusBadge label={runLabel(r.status)} />
          <Button size="sm" variant="ghost" onClick={() => onOpenRun(r.id)}>
            상세
          </Button>
        </InfoRow>
      ))}
    </Panel>
  );
}
